import { useState, useEffect, useRef } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loader2, RefreshCw, ArrowRight, Check, Cloud, AlertCircle, Upload } from 'lucide-react'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import { formatFileSize } from '@/lib/schemas'
import { toast } from 'sonner'
import type { LibraryDocument } from '@/lib/schemas'

interface ReplaceDocumentDialogProps {
  document: LibraryDocument
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Slice an ISO or 'YYYY-MM-DD' string down to the date portion an
 *  `<input type="date">` expects; empty for null/undefined. */
function toDateInput(value: string | null | undefined): string {
  if (!value) return ''
  return value.slice(0, 10)
}

export function ReplaceDocumentDialog({
  document,
  open,
  onOpenChange,
}: ReplaceDocumentDialogProps) {
  const { replaceDocument, fetchDocuments } = useLibraryStore()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [effectiveDate, setEffectiveDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [isReplacing, setIsReplacing] = useState(false)
  const [conflictError, setConflictError] = useState<string | null>(null)

  const currentVersion = document.version ?? 1
  const nextVersion = currentVersion + 1

  // Reset transient state + prefill dates from the doc each time it opens.
  useEffect(() => {
    if (open) {
      setFile(null)
      setConflictError(null)
      setIsReplacing(false)
      setEffectiveDate(toDateInput(document.effectiveDate))
      setExpiryDate(toDateInput(document.expiryDate))
    }
  }, [open, document.effectiveDate, document.expiryDate])

  const handleReplace = async () => {
    if (!file) return
    setIsReplacing(true)
    setConflictError(null)
    try {
      await replaceDocument(document.id, file, currentVersion, {
        effectiveDate: effectiveDate || null,
        expiryDate: expiryDate || null,
      })
      toast.success('New version uploaded — re-indexing')
      onOpenChange(false)
    } catch (error) {
      // Optimistic-concurrency conflict → inline "refresh and retry"; anything
      // else surfaces as a toast so the dialog stays usable for a retry.
      if (error instanceof ApiRequestError && error.statusCode === 409) {
        setConflictError(error.message)
      } else {
        const message = error instanceof Error ? error.message : 'Failed to replace document'
        toast.error(message)
      }
    } finally {
      setIsReplacing(false)
    }
  }

  const handleRefresh = () => {
    void fetchDocuments()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Upload new version</DialogTitle>
          <DialogDescription>
            Replacing <span className="font-medium text-foreground">{document.name}</span>. The old
            file is retained for download; its chunks are removed from AI retrieval and re-indexed
            from the new bytes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Hidden native picker driven by the button / file summary. */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            aria-label="Choose replacement file"
            onChange={(e) => {
              const selected = e.target.files?.[0] ?? null
              setFile(selected)
              setConflictError(null)
            }}
          />

          {file ? (
            <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3">
              <FileTypeIcon mimeType={file.type} size={20} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{file.name}</p>
                <p className="text-meta tabular-nums">
                  {formatFileSize(file.size)} · selected just now
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" dot={false}>v{currentVersion}</Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Badge
                  variant="outline"
                  dot={false}
                  className="border-[hsl(var(--brand))] text-[hsl(var(--brand))]"
                >
                  v{nextVersion}
                </Badge>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose a file…
            </Button>
          )}

          {/* Effective / expiry — pre-filled from the row, editable here. */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="replace-effective-date">Effective date</Label>
              <Input
                id="replace-effective-date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="replace-expiry-date">Expiry date</Label>
              <Input
                id="replace-expiry-date"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>

          {/* Carry-over note — folder/tags/Fact Bank survive the swap. */}
          <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-meta">
            <Check className="h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
            <span>
              Folder, tags and <span className="font-medium text-foreground">Fact Bank</span> status
              carry over to the new version.
            </span>
          </div>

          {/* SharePoint provenance — only for imported docs; the row pill is gone. */}
          {document.source === 'sharepoint' && (
            <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-meta">
              <Cloud className="h-4 w-4 shrink-0" />
              <span>
                Originally imported from <span className="font-medium text-foreground">SharePoint</span>{' '}
                — check there for the latest version.
              </span>
            </div>
          )}

          {/* 409 conflict — refresh-and-retry, not a silent overwrite. */}
          {conflictError && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-destructive">
                    Couldn't replace — version conflict
                  </p>
                  <p className="mt-0.5 text-meta">{conflictError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handleRefresh}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isReplacing}>
            Cancel
          </Button>
          <Button onClick={() => void handleReplace()} disabled={!file || isReplacing}>
            {isReplacing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Replace &amp; re-index
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
